/**
 * TaskPortalActivityFeedService
 * Service for the TaskManagement activity drawer.
 * Route: api/task-portal/activity-feed
 */
import { TaskPortalBaseService } from './TaskPortalBaseService';
/**
 * TaskPortalActivityFeedService
 *
 * Aggregates comments and notes across every task the current user creates, is
 * assigned, is looped in on, or is @-mentioned in, and tracks which items they
 * have seen.
 *
 * Scope is derived server-side from the caller's own memberships, so there is no
 * user id to pass and no way to request another user's feed.
 */
export class TaskPortalActivityFeedService extends TaskPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? TaskPortalActivityFeedService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? TaskPortalActivityFeedService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * The grouped activity feed for the current user.
     * GET /api/task-portal/activity-feed
     */
    async getFeed() {
        const response = await this.getFeedRaw();
        return (response.data ?? {
            totalUnread: 0,
            unreadComments: 0,
            unreadNotes: 0,
            groups: [],
        });
    }
    getFeedRaw() {
        return this.get('');
    }
    /**
     * Unread counts only — cheaper than the full feed, for the drawer's badge.
     * GET /api/task-portal/activity-feed/unread-count
     */
    async getUnreadCount() {
        const response = await this.getUnreadCountRaw();
        return response.data ?? { totalUnread: 0, unreadComments: 0, unreadNotes: 0 };
    }
    getUnreadCountRaw() {
        return this.get('unread-count');
    }
    /**
     * Mark a batch of items seen. Idempotent — the drawer buffers newly-seen items
     * and replays that buffer after a reconnect, so repeats are expected and cheap.
     * Returns the number of receipts actually created.
     * POST /api/task-portal/activity-feed/seen
     */
    async markSeen(items) {
        const response = await this.markSeenRaw(items);
        return response.data ?? 0;
    }
    markSeenRaw(items) {
        return this.post('seen', items);
    }
    /**
     * Mark every item in the current user's feed seen.
     * POST /api/task-portal/activity-feed/seen/all
     */
    async markAllSeen() {
        const response = await this.markAllSeenRaw();
        return response.data ?? 0;
    }
    markAllSeenRaw() {
        return this.post('seen/all');
    }
    /**
     * Mark every item on one task seen. Called when the user opens that task.
     * POST /api/task-portal/activity-feed/tasks/{taskId}/seen
     */
    async markTaskSeen(taskId) {
        const response = await this.markTaskSeenRaw(taskId);
        return response.data ?? 0;
    }
    markTaskSeenRaw(taskId) {
        return this.post(`tasks/${taskId}/seen`);
    }
    /**
     * The users who may be @-mentioned on a task — its creator, active assignees
     * and looped-in users. Deliberately not the org directory: mentioning someone
     * without task access would give them a feed row they cannot open.
     * GET /api/task-portal/activity-feed/tasks/{taskId}/mentionable
     */
    async getMentionableUsers(taskId) {
        const response = await this.getMentionableUsersRaw(taskId);
        return response.data ?? [];
    }
    getMentionableUsersRaw(taskId) {
        return this.get(`tasks/${taskId}/mentionable`);
    }
}
TaskPortalActivityFeedService.DEFAULT_SERVICE_PATH = 'tasks';
TaskPortalActivityFeedService.BASE_PATH = 'api/task-portal/activity-feed';
//# sourceMappingURL=TaskPortalActivityFeedService.js.map